import * as z from "zod";
import { code, decimal, pagination } from "../index";
import { paymentCategories } from "../reference/enums";
import { creditOutcomes, soStatuses } from "./enums";

// Spec v0.3 §9, "Proiezione degli incassi" (C-55, P5.11d, ADR-261): the cash the open orders of Nexxt still bring, in
// the format of the CFO. The invoices are Business Central's in F1 (its own report): this is the part of the orders,
// what is not yet released to the warehouse. A view of Tomorrow, served by sales, which owns the orders (ADR-082).

/**
 * When the cash of a part of an order is expected, as the CFO reads the aging of NetSuite: past due by days, then the
 * weeks from today, `w0` from today to Sunday and `w1`…`w12` from Monday to Sunday, then beyond the twelfth week.
 */
export const cashProjectionBuckets = [
  "overdue_over_90",
  "overdue_61_90",
  "overdue_31_60",
  "overdue_1_30",
  "w0",
  "w1",
  "w2",
  "w3",
  "w4",
  "w5",
  "w6",
  "w7",
  "w8",
  "w9",
  "w10",
  "w11",
  "w12",
  "later",
] as const;
export type CashProjectionBucket = (typeof cashProjectionBuckets)[number];

/**
 * The two parts of an order: `ready` to ship, its goods frozen and not yet released (a proforma is due at once, in
 * `w0`); `window`, the rest of what it still has to ship, due from its delivery window.
 */
export const cashProjectionParts = ["ready", "window"] as const;
export type CashProjectionPart = (typeof cashProjectionParts)[number];

/**
 * Why a part by delivery window is out of the projection and listed apart: the order has no delivery date, or its
 * window closed more than three months ago.
 */
export const cashProjectionExclusions = ["no_date", "window_closed"] as const;
export type CashProjectionExclusion = (typeof cashProjectionExclusions)[number];

const projectionFilters = {
  /** Code of the company that sells: only its orders. */
  legalEntity: code.optional(),
  brand: z.uuid().optional(),
  customer: z.uuid().optional(),
};

/** The projection as a table: the amounts in euro of each period, by part. */
export const getCashProjectionInput = z.object(projectionFilters);
export type GetCashProjectionInput = z.input<typeof getCashProjectionInput>;

/**
 * The parts of orders behind a cell of the projection, the soonest first: those of a period, of a part, or those left
 * out for a reason (which have no period: with `bucket` too, none).
 */
export const listCashProjectionOrdersInput = z.object({
  ...projectionFilters,
  bucket: z.enum(cashProjectionBuckets).optional(),
  part: z.enum(cashProjectionParts).optional(),
  excluded: z.enum(cashProjectionExclusions).optional(),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListCashProjectionOrdersInput = z.input<typeof listCashProjectionOrdersInput>;

/** An amount in euro, at the exchange rate of each order, and how many parts of orders make it. */
export const cashProjectionAmountDto = z.object({ amount: decimal, orderCount: z.number().int() });
export type CashProjectionAmountDto = z.output<typeof cashProjectionAmountDto>;

/** A period of the projection, with its days (open at one end for the oldest and the latest) and its amounts. */
export const cashProjectionBucketDto = z.object({
  bucket: z.enum(cashProjectionBuckets),
  from: z.iso.date().nullable(),
  to: z.iso.date().nullable(),
  ready: cashProjectionAmountDto,
  window: cashProjectionAmountDto,
  total: cashProjectionAmountDto,
});
export type CashProjectionBucketDto = z.output<typeof cashProjectionBucketDto>;

export const cashProjectionDto = z
  .object({
    /** The day the periods count from (UTC). */
    asOf: z.iso.date(),
    /** The currency of every amount: each order converted at its own rate, the one of its season (ADR-179). */
    currency: z.string(),
    /** The parts by delivery window whose window ended before this day are out (`window_closed`). */
    closedBefore: z.iso.date(),
    buckets: z.array(cashProjectionBucketDto),
    total: z.object({
      ready: cashProjectionAmountDto,
      window: cashProjectionAmountDto,
      total: cashProjectionAmountDto,
    }),
    /** The parts by delivery window left out, by reason, listed apart. */
    excluded: z.array(cashProjectionAmountDto.extend({ reason: z.enum(cashProjectionExclusions) })),
    /** Parts of orders without an exchange rate to the euro: in the lists, out of the amounts. */
    withoutRate: z.number().int(),
  })
  .meta({ id: "CashProjection" });
export type CashProjectionDto = z.output<typeof cashProjectionDto>;

/** A part of an order in the projection, with how its amount comes out. */
export const cashProjectionOrderDto = z
  .object({
    salesOrderId: z.uuid(),
    soNumber: z.string(),
    part: z.enum(cashProjectionParts),
    status: z.enum(soStatuses),
    creditOutcome: z.enum(creditOutcomes),
    customerOrgId: z.uuid(),
    customer: z.string(),
    brand: z.string().nullable(),
    season: z.string().nullable(),
    /** Code of the company that sells. */
    legalEntity: z.string(),
    paymentTerms: z.string().nullable(),
    paymentCategory: z.enum(paymentCategories).nullable(),
    netDays: z.number().int().nullable(),
    expectedDeliveryStart: z.iso.date().nullable(),
    expectedDeliveryEnd: z.iso.date().nullable(),
    /** When its cash is expected; null for a part left out. */
    expectedOn: z.iso.date().nullable(),
    bucket: z.enum(cashProjectionBuckets).nullable(),
    /** Days past `expectedOn`, for a part past due. */
    daysOverdue: z.number().int().nullable(),
    excluded: z.enum(cashProjectionExclusions).nullable(),
    currency: z.string(),
    /** What the part is worth: its units after the discounts, in the currency of the order. */
    partAmount: decimal,
    /** The deposits received for the order that this part takes off (ready first, then the rest). */
    depositApplied: decimal,
    /** `partAmount` less `depositApplied`. */
    expectedAmount: decimal,
    /** The rate of the order to the euro; null when Finance has none for its season. */
    exchangeRate: decimal.nullable(),
    expectedAmountEur: decimal.nullable(),
  })
  .meta({ id: "CashProjectionOrder" });
export type CashProjectionOrderDto = z.output<typeof cashProjectionOrderDto>;
